import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { ShieldCheck, Lock, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAdmin } from "@/context/AdminContext";

const AdminLogin = () => {
  const { login, isAdmin } = useAdmin();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@bharaticrafts.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const expired = new URLSearchParams(window.location.search).get("expired") === "1";

  if (isAdmin) return <Navigate to="/admin/products" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email.trim(), password);
      toast.success("Welcome back, Admin");
      navigate("/admin/products");
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-[70vh] flex items-center justify-center px-4 py-10" data-testid="admin-login-page">
      <div className="w-full max-w-sm bg-white rounded-sm fk-shadow p-6 fk-fade-up">
        <div className="flex flex-col items-center mb-5">
          <span className="w-12 h-12 rounded-full bg-[#C62828] flex items-center justify-center text-white">
            <ShieldCheck className="w-6 h-6" />
          </span>
          <h1 className="font-head text-xl font-bold text-[#212121] mt-3">BharatiCrafts Admin</h1>
          <p className="text-xs text-[#878787]">Sign in to manage your catalog</p>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {expired && !error && (
            <p className="text-xs text-[#a16207] bg-[#fef9c3] rounded-sm px-3 py-2" data-testid="admin-session-expired-message">
              Your session expired. Please sign in again.
            </p>
          )}
          <div className="relative">
            <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#878787]" />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Admin email"
              required
              className="w-full h-11 border border-[#c2c2c2] rounded-sm pl-9 pr-3 text-sm outline-none focus:border-[#C62828]"
              data-testid="admin-login-email-input"
            />
          </div>
          <div className="relative">
            <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#878787]" />
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              required
              className="w-full h-11 border border-[#c2c2c2] rounded-sm pl-9 pr-3 text-sm outline-none focus:border-[#C62828]"
              data-testid="admin-login-password-input"
            />
          </div>

          {error && (
            <p className="text-xs text-red-600" data-testid="admin-login-error-message">{error}</p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#C62828] text-white font-semibold h-11 rounded-sm hover:bg-[#A01F1F] transition-colors disabled:opacity-60"
            data-testid="admin-login-submit-button"
          >
            {busy ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <p className="text-[11px] text-[#878787] mt-4 text-center">
          Default: admin@bharaticrafts.com / admin123_secure
        </p>
      </div>
    </main>
  );
};

export default AdminLogin;
